import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, UserX, Trash2, Circle, Check, X, Clock, UserCheck, TrendingUp, Phone, Target, Calendar, Loader2 } from 'lucide-react';
import CreateUserModal from '../components/CreateUserModal';
import { formatDate } from '../lib/utils';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [onlineStatus, setOnlineStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
    fetchOnlineStatus();
    
    // Refresh online status every 30 seconds
    const statusInterval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(statusInterval);
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = roleFilter !== 'all' ? roleFilter : null;
      const response = await api.getUsers(params);
      // Filter out pending users
      setUsers(response.data.filter(u => u.is_approved !== false));
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await api.getPendingUsers();
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    }
  };

  const fetchOnlineStatus = async () => {
    try {
      const response = await api.getOnlineStatus();
      const statusMap = {};
      response.data.forEach(s => {
        statusMap[s.id] = s;
      });
      setOnlineStatus(statusMap);
    } catch (error) {
      console.error('Failed to fetch online status:', error);
    }
  };

  const handleApproveUser = async (userId, userName) => {
    setProcessingUserId(userId);
    try {
      await api.approveUser(userId);
      toast.success(`${userName} has been approved!`);
      fetchUsers();
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleRejectUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}'s registration?`)) return;
    
    setProcessingUserId(userId);
    try {
      await api.rejectUser(userId);
      toast.success(`${userName}'s registration has been rejected`);
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleDeleteUser = async (userId, userName, userRole) => {
    if (user.role === 'manager' && userRole !== 'caller') {
      toast.error('Managers can only delete caller accounts');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${userName}? All their assigned leads will be unassigned.`)) {
      setDeletingUserId(userId);
      try {
        const response = await api.deleteUser(userId);
        toast.success(response.data.message);
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      } finally {
        setDeletingUserId(null);
      }
    }
  };

  const handleViewPerformance = async (callerId) => {
    setShowPerformanceModal(callerId);
    setLoadingPerformance(true);
    try {
      const response = await api.getCallerPerformance(callerId);
      setPerformanceData(response.data);
    } catch (error) {
      toast.error('Failed to fetch performance data');
      setShowPerformanceModal(null);
    } finally {
      setLoadingPerformance(false);
    }
  };

  if (!['admin', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <UserX size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-600 font-body">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading users...</div>
      </div>
    );
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'manager':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'caller':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'complaint_entry':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'complaint_solver':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'manager': return 'Manager';
      case 'caller': return 'Caller';
      case 'complaint_entry': return 'Complaint Entry';
      case 'complaint_solver': return 'Complaint Solver';
      default: return role;
    }
  };

  const onlineCallers = users.filter(u => u.role === 'caller' && onlineStatus[u.id]?.is_online).length;
  const totalCallers = users.filter(u => u.role === 'caller').length;

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            User Management
          </h1>
          <p className="text-slate-600 font-body mt-1">
            {users.length} user{users.length !== 1 ? 's' : ''} registered
            {totalCallers > 0 && (
              <span className="ml-2">
                • <span className="text-green-600 font-semibold">{onlineCallers}</span>/{totalCallers} callers online
              </span>
            )}
            {pendingUsers.length > 0 && (
              <span className="ml-2">
                • <span className="text-amber-600 font-semibold">{pendingUsers.length}</span> pending approval
              </span>
            )}
          </p>
        </div>

        <button
          data-testid="create-user-button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
        >
          <Plus size={18} />
          <span>Add User</span>
        </button>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 rounded-xl border-2 border-amber-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-amber-100 border-b border-amber-200">
            <h2 className="text-lg font-semibold text-amber-800 font-body flex items-center gap-2">
              <Clock size={20} />
              Pending Approvals ({pendingUsers.length})
            </h2>
          </div>
          <div className="divide-y divide-amber-200">
            {pendingUsers.map((pendingUser) => (
              <div key={pendingUser.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900 font-body">{pendingUser.name}</p>
                  <p className="text-sm text-slate-600 font-body">{pendingUser.email}</p>
                  {pendingUser.phone && (
                    <p className="text-sm text-slate-500 font-mono">{pendingUser.phone}</p>
                  )}
                  <p className="text-xs text-slate-400 font-body mt-1">
                    Registered: {formatDate(pendingUser.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApproveUser(pendingUser.id, pendingUser.name)}
                    disabled={processingUserId === pendingUser.id}
                    data-testid={`approve-user-${pendingUser.id}`}
                    className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 font-body"
                  >
                    <Check size={16} />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => handleRejectUser(pendingUser.id, pendingUser.name)}
                    disabled={processingUserId === pendingUser.id}
                    data-testid={`reject-user-${pendingUser.id}`}
                    className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 font-body"
                  >
                    <X size={16} />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-slate-700 font-body">Filter by Role:</label>
          <select
            data-testid="role-filter-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
          >
            <option value="all">All Roles</option>
            <optgroup label="Sales Calling">
              <option value="caller">Caller</option>
              <option value="manager">Manager</option>
            </optgroup>
            <optgroup label="Complaints">
              <option value="complaint_entry">Complaint Entry</option>
              <option value="complaint_solver">Complaint Solver</option>
            </optgroup>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Last Seen
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-700 font-body">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500 font-body">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const canDelete = u.id !== user.id && (user.role === 'admin' || (user.role === 'manager' && u.role === 'caller'));
                  const status = onlineStatus[u.id];
                  const isOnline = status?.is_online || false;
                  const isClickable = u.role === 'caller';
                  
                  return (
                    <tr 
                      key={u.id} 
                      data-testid={`user-row-${u.id}`} 
                      className={`hover:bg-slate-50 transition-colors ${isClickable ? 'cursor-pointer' : ''}`}
                      onClick={() => isClickable && handleViewPerformance(u.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 font-body">{u.name}</p>
                          {isClickable && (
                            <TrendingUp size={14} className="text-slate-400" title="Click to view performance" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-slate-600 font-body text-sm">{u.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full border ${getRoleBadgeColor(
                            u.role
                          )}`}
                        >
                          {getRoleDisplayName(u.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {u.role === 'caller' ? (
                          <div className="flex items-center gap-2">
                            <Circle 
                              size={10} 
                              className={isOnline ? 'fill-green-500 text-green-500' : 'fill-slate-300 text-slate-300'} 
                            />
                            <span className={`text-sm font-body ${isOnline ? 'text-green-600 font-semibold' : 'text-slate-500'}`}>
                              {isOnline ? 'Online' : 'Offline'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm font-body">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {u.role === 'caller' && status?.last_seen ? (
                          <p className="text-slate-600 font-mono text-sm">
                            {formatDate(status.last_seen)}
                          </p>
                        ) : (
                          <span className="text-slate-400 text-sm font-body">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        {canDelete ? (
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={() => handleDeleteUser(u.id, u.name, u.role)}
                            disabled={deletingUserId === u.id}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 font-body"
                          >
                            <Trash2 size={16} />
                            <span>{deletingUserId === u.id ? 'Deleting...' : 'Delete'}</span>
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-body">
                            {u.id === user.id ? 'You' : 'Protected'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {/* Caller Performance Modal */}
      {showPerformanceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold font-headings text-slate-900">Caller Performance</h2>
                {performanceData && (
                  <p className="text-slate-600 font-body mt-1">{performanceData.caller.name}</p>
                )}
              </div>
              <button 
                onClick={() => { setShowPerformanceModal(null); setPerformanceData(null); }} 
                className="p-2 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {loadingPerformance ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 size={32} className="animate-spin text-slate-400" />
              </div>
            ) : performanceData ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={16} />
                      <span className="text-xs font-medium uppercase">Conversion Rate</span>
                    </div>
                    <p className="text-3xl font-bold">{performanceData.total.conversion_rate}%</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Target size={16} />
                      <span className="text-xs font-medium uppercase">Total Revenue</span>
                    </div>
                    <p className="text-3xl font-bold">₹{(performanceData.total.revenue || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone size={16} />
                      <span className="text-xs font-medium uppercase">Total Leads</span>
                    </div>
                    <p className="text-3xl font-bold">{performanceData.total.leads}</p>
                  </div>
                </div>

                {/* Daily & Monthly Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <h3 className="text-sm font-bold uppercase text-slate-700 mb-3 flex items-center gap-2">
                      <Calendar size={16} />
                      Today's Performance
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-body">Sales</span>
                        <span className="font-bold text-slate-900">{performanceData.daily.customers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-body">Revenue</span>
                        <span className="font-bold text-emerald-600">₹{(performanceData.daily.revenue || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <h3 className="text-sm font-bold uppercase text-slate-700 mb-3 flex items-center gap-2">
                      <Target size={16} />
                      This Month
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-body">Sales</span>
                        <span className="font-bold text-slate-900">{performanceData.monthly.customers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-body">Revenue</span>
                        <span className="font-bold text-emerald-600">₹{(performanceData.monthly.revenue || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Status Breakdown */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <h3 className="text-sm font-bold uppercase text-slate-700 mb-4">Lead Status Breakdown</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{performanceData.total.hot_leads}</p>
                      <p className="text-xs text-red-600 font-medium uppercase">Hot</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{performanceData.total.warm_leads}</p>
                      <p className="text-xs text-amber-600 font-medium uppercase">Warm</p>
                    </div>
                    <div className="text-center p-3 bg-slate-100 rounded-lg">
                      <p className="text-2xl font-bold text-slate-600">{performanceData.total.cold_leads}</p>
                      <p className="text-xs text-slate-600 font-medium uppercase">Cold</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{performanceData.total.followups}</p>
                      <p className="text-xs text-blue-600 font-medium uppercase">Follow Up</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{performanceData.total.customers}</p>
                      <p className="text-xs text-green-600 font-medium uppercase">Customers</p>
                    </div>
                    <div className="text-center p-3 bg-indigo-50 rounded-lg">
                      <p className="text-2xl font-bold text-indigo-600">{performanceData.total.new_leads}</p>
                      <p className="text-xs text-indigo-600 font-medium uppercase">New Leads</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
